import { useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Mail,
  Plus,
  Globe,
  Server,
  Trash2,
  Star,
  Send,
  RefreshCw,
  Copy,
  CheckCircle2,
  AlertTriangle,
  Clock,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  tenantEmailService,
  type EmailIdentity,
  type EmailTransport,
  type DnsRecord,
} from '@/services/platform/tenantEmail';

const QK = ['tenant', 'email-identities'] as const;

/**
 * Settings → Email senders.
 *
 * Tenant-owned outbound email. A tenant configures + operates their OWN
 * sending identity — their own domain (verified via DNS, sent through the
 * platform's SES with no platform identity on the mail) or their own SMTP
 * server. The platform sender (no-reply@…zafroi.com) is reserved for
 * platform↔user mail and never used for tenant→customer email.
 */
export function EmailSendersPage() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const listQuery = useQuery({ queryKey: QK, queryFn: tenantEmailService.list });

  const invalidate = () => qc.invalidateQueries({ queryKey: QK });

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-8">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="grid h-12 w-12 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <Mail className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-[20px] font-semibold leading-tight tracking-tight">
              Email senders
            </h1>
            <p className="max-w-xl text-[13px] text-muted-foreground">
              Send customer email from your own domain or mail server. You own
              and configure the sender — verify your domain (or connect your SMTP
              server) and every support reply goes out as you, not the platform.
            </p>
          </div>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Add sender
        </Button>
      </header>

      {listQuery.isLoading ? (
        <div className="text-[13px] text-muted-foreground">Loading…</div>
      ) : listQuery.data && listQuery.data.length > 0 ? (
        <div className="space-y-3">
          {listQuery.data.map((identity) => (
            <IdentityCard
              key={identity.id}
              identity={identity}
              onChanged={invalidate}
            />
          ))}
        </div>
      ) : (
        <EmptyState onAdd={() => setAddOpen(true)} />
      )}

      <AddSenderDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={invalidate}
      />
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-lg border border-dashed border-border/70 bg-card/50 px-6 py-10 text-center">
      <Mail className="mx-auto h-8 w-8 text-muted-foreground/50" />
      <p className="mt-3 text-[14px] font-medium">No sending identity yet</p>
      <p className="mx-auto mt-1 max-w-md text-[12.5px] text-muted-foreground">
        Add your own domain (best deliverability) or your own SMTP server. Until
        then, customer email cannot be sent from your brand.
      </p>
      <Button size="sm" className="mt-4" onClick={onAdd}>
        <Plus className="mr-1.5 h-4 w-4" /> Add your first sender
      </Button>
    </div>
  );
}

// ───────────────────────── identity card ─────────────────────────

function IdentityCard({
  identity,
  onChanged,
}: {
  identity: EmailIdentity;
  onChanged: () => void;
}) {
  const [testOpen, setTestOpen] = useState(false);

  const setDefault = useMutation({
    mutationFn: () => tenantEmailService.setDefault(identity.id),
    onSuccess: () => {
      toast.success(`"${identity.label}" is now the default sender`);
      onChanged();
    },
    onError: (e: unknown) => toast.error(errMsg(e)),
  });

  const refreshDomain = useMutation({
    mutationFn: () => tenantEmailService.refreshDomain(identity.id),
    onSuccess: (res) => {
      toast.success(
        res.domainStatus === 'VERIFIED'
          ? 'Domain verified — sender is active'
          : 'Not verified yet — DNS can take a few minutes to propagate',
      );
      onChanged();
    },
    onError: (e: unknown) => toast.error(errMsg(e)),
  });

  const remove = useMutation({
    mutationFn: () => tenantEmailService.remove(identity.id),
    onSuccess: () => {
      toast.success('Sender removed');
      onChanged();
    },
    onError: (e: unknown) => toast.error(errMsg(e)),
  });

  const isSes = identity.transport === 'SES_DELEGATED';
  const showDns =
    isSes && identity.domainStatus !== 'VERIFIED' && !!identity.dnsRecords?.length;

  return (
    <div className="rounded-lg border border-border/70 bg-card px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 place-items-center rounded-md bg-muted text-muted-foreground">
            {isSes ? <Globe className="h-4 w-4" /> : <Server className="h-4 w-4" />}
          </span>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-semibold">{identity.label}</span>
              {identity.isDefault && (
                <Badge variant="muted" className="gap-1 text-[10px]">
                  <Star className="h-3 w-3 fill-current" /> Default
                </Badge>
              )}
              <StatusBadge status={identity.status} />
            </div>
            <div className="text-[12.5px] text-muted-foreground">
              {identity.fromName ? `${identity.fromName} · ` : ''}
              <span className="font-mono">{identity.fromAddress}</span>
            </div>
            <div className="text-[11px] text-muted-foreground">
              {isSes ? 'Own domain (SES)' : 'Own SMTP server'}
              {identity.replyTo ? ` · reply-to ${identity.replyTo}` : ''}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setTestOpen(true)}
            title="Send a test email"
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
          {!identity.isDefault && (
            <Button
              size="sm"
              variant="ghost"
              disabled={setDefault.isPending}
              onClick={() => setDefault.mutate()}
              title="Make default sender"
            >
              <Star className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm(`Remove sender "${identity.label}"?`)) remove.mutate();
            }}
            title="Remove"
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      </div>

      {identity.lastError && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-[11.5px] text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{identity.lastError}</span>
        </div>
      )}

      {showDns && (
        <DnsPanel
          domain={identity.domain ?? ''}
          records={identity.dnsRecords ?? []}
          status={identity.domainStatus}
          onRecheck={() => refreshDomain.mutate()}
          rechecking={refreshDomain.isPending}
        />
      )}

      <TestSendDialog
        open={testOpen}
        onOpenChange={setTestOpen}
        identity={identity}
        onTested={onChanged}
      />
    </div>
  );
}

function DnsPanel({
  domain,
  records,
  status,
  onRecheck,
  rechecking,
}: {
  domain: string;
  records: DnsRecord[];
  status: string;
  onRecheck: () => void;
  rechecking: boolean;
}) {
  return (
    <div className="mt-3 rounded-md border border-info/20 bg-info/5 px-3 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[12px] font-medium">
          <Clock className="h-3.5 w-3.5 text-info" />
          Publish these DNS records for{' '}
          <span className="font-mono">{domain}</span>
          <Badge variant="muted" className="text-[10px]">
            {status}
          </Badge>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onRecheck}
          disabled={rechecking}
        >
          <RefreshCw
            className={`mr-1.5 h-3.5 w-3.5 ${rechecking ? 'animate-spin' : ''}`}
          />
          Re-check
        </Button>
      </div>
      <p className="mt-1.5 text-[11.5px] text-muted-foreground">
        Add these at your DNS provider. Verification + DKIM are required;
        SPF/DMARC are recommended for deliverability. Changes can take a few
        minutes to propagate.
      </p>
      <div className="mt-2.5 space-y-1.5">
        {records.map((r, i) => (
          <DnsRow key={i} record={r} />
        ))}
      </div>
    </div>
  );
}

function DnsRow({ record }: { record: DnsRecord }) {
  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied'),
      () => toast.error('Could not copy'),
    );
  };
  return (
    <div className="flex items-center gap-2 rounded border border-border/60 bg-background px-2.5 py-1.5 text-[11px]">
      <Badge variant="muted" className="shrink-0 font-mono text-[10px]">
        {record.type}
      </Badge>
      {!record.required && (
        <Badge variant="muted" className="shrink-0 text-[9.5px] opacity-70">
          optional
        </Badge>
      )}
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Name:</span>
          <code className="truncate font-mono">{record.name}</code>
          <button
            type="button"
            onClick={() => copy(record.name)}
            className="text-muted-foreground hover:text-foreground"
          >
            <Copy className="h-3 w-3" />
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Value:</span>
          <code className="truncate font-mono">{record.value}</code>
          <button
            type="button"
            onClick={() => copy(record.value)}
            className="text-muted-foreground hover:text-foreground"
          >
            <Copy className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: EmailIdentity['status'] }) {
  if (status === 'ACTIVE')
    return (
      <Badge variant="muted" className="gap-1 text-[10px] text-success">
        <CheckCircle2 className="h-3 w-3" /> Active
      </Badge>
    );
  if (status === 'FAILED')
    return (
      <Badge variant="muted" className="gap-1 text-[10px] text-destructive">
        <AlertTriangle className="h-3 w-3" /> Failed
      </Badge>
    );
  if (status === 'DISCONNECTED')
    return (
      <Badge variant="muted" className="text-[10px]">
        Disconnected
      </Badge>
    );
  return (
    <Badge variant="muted" className="gap-1 text-[10px]">
      <Clock className="h-3 w-3" /> Pending
    </Badge>
  );
}

// ───────────────────────── add dialog ─────────────────────────

function AddSenderDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [transport, setTransport] = useState<EmailTransport>('SES_DELEGATED');
  const [label, setLabel] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  // SMTP
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [secure, setSecure] = useState(false);
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');

  const reset = () => {
    setTransport('SES_DELEGATED');
    setLabel('');
    setFromAddress('');
    setFromName('');
    setReplyTo('');
    setHost('');
    setPort('587');
    setSecure(false);
    setUser('');
    setPass('');
  };

  const create = useMutation({
    mutationFn: () =>
      tenantEmailService.create({
        label: label.trim(),
        transport,
        fromAddress: fromAddress.trim(),
        fromName: fromName.trim() || undefined,
        replyTo: replyTo.trim() || undefined,
        ...(transport === 'SMTP'
          ? {
              smtp: {
                host: host.trim(),
                port: Number(port),
                secure,
                user: user.trim(),
                pass,
              },
            }
          : {}),
      }),
    onSuccess: () => {
      toast.success(
        transport === 'SES_DELEGATED'
          ? 'Sender added — publish the DNS records to verify'
          : 'Sender added',
      );
      reset();
      onOpenChange(false);
      onCreated();
    },
    onError: (e: unknown) => toast.error(errMsg(e)),
  });

  const canSubmit =
    label.trim() &&
    fromAddress.includes('@') &&
    (transport !== 'SMTP' || (host.trim() && user.trim() && pass && port));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add email sender</DialogTitle>
          <DialogDescription>
            Configure a sender you own. We never send your customer email from a
            platform address.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <TransportChoice
              active={transport === 'SES_DELEGATED'}
              icon={<Globe className="h-4 w-4" />}
              title="Own domain"
              blurb="Verify your domain via DNS. Best deliverability."
              onClick={() => setTransport('SES_DELEGATED')}
            />
            <TransportChoice
              active={transport === 'SMTP'}
              icon={<Server className="h-4 w-4" />}
              title="Own SMTP"
              blurb="Relay through your own mail server."
              onClick={() => setTransport('SMTP')}
            />
          </div>

          <Field label="Label">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Support"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From address">
              <Input
                value={fromAddress}
                onChange={(e) => setFromAddress(e.target.value)}
                placeholder="support@yourcompany.com"
              />
            </Field>
            <Field label="From name (optional)">
              <Input
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Acme Support"
              />
            </Field>
          </div>
          <Field label="Reply-to (optional)">
            <Input
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="help@yourcompany.com"
            />
          </Field>

          {transport === 'SES_DELEGATED' ? (
            <p className="rounded-md border border-info/20 bg-info/5 px-3 py-2 text-[11.5px] text-muted-foreground">
              After saving, we'll show DNS records to publish for{' '}
              <span className="font-mono">
                {fromAddress.includes('@')
                  ? fromAddress.split('@')[1]
                  : 'your domain'}
              </span>
              . Once they verify, the sender goes active.
            </p>
          ) : (
            <div className="space-y-3 rounded-md border border-border/60 bg-muted/30 p-3">
              <div className="grid grid-cols-[1fr_auto_auto] items-end gap-3">
                <Field label="SMTP host">
                  <Input
                    value={host}
                    onChange={(e) => setHost(e.target.value)}
                    placeholder="smtp.yourprovider.com"
                  />
                </Field>
                <Field label="Port">
                  <Input
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    className="w-20"
                  />
                </Field>
                <label className="flex items-center gap-1.5 pb-2 text-[12px]">
                  <input
                    type="checkbox"
                    checked={secure}
                    onChange={(e) => setSecure(e.target.checked)}
                  />
                  TLS
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Username">
                  <Input
                    value={user}
                    onChange={(e) => setUser(e.target.value)}
                    placeholder="apikey / mailbox"
                  />
                </Field>
                <Field label="Password">
                  <Input
                    type="password"
                    value={pass}
                    onChange={(e) => setPass(e.target.value)}
                  />
                </Field>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Credentials are encrypted at rest. Port 465 → enable TLS; 587 →
                leave off (STARTTLS).
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Saving…' : 'Add sender'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransportChoice({
  active,
  icon,
  title,
  blurb,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  title: string;
  blurb: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-3 py-2.5 text-left transition-colors ${
        active
          ? 'border-primary/40 ring-1 ring-inset ring-primary/15'
          : 'border-border/70 hover:border-border'
      }`}
    >
      <div className="flex items-center gap-2 text-[13px] font-semibold">
        {icon}
        {title}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{blurb}</p>
    </button>
  );
}

function TestSendDialog({
  open,
  onOpenChange,
  identity,
  onTested,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  identity: EmailIdentity;
  onTested: () => void;
}) {
  const [to, setTo] = useState('');
  const test = useMutation({
    mutationFn: () => tenantEmailService.test(identity.id, to.trim()),
    onSuccess: () => {
      toast.success('Test email sent');
      onOpenChange(false);
      setTo('');
      onTested();
    },
    onError: (e: unknown) => toast.error(errMsg(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send a test email</DialogTitle>
          <DialogDescription>
            Sent through "{identity.label}" ({identity.fromAddress}).
          </DialogDescription>
        </DialogHeader>
        <Field label="Send to">
          <Input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="you@yourcompany.com"
          />
        </Field>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!to.includes('@') || test.isPending}
            onClick={() => test.mutate()}
          >
            {test.isPending ? 'Sending…' : 'Send test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      {children}
    </div>
  );
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : 'Something went wrong';
}
